import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import { FaTrash, FaEdit, FaFilePdf, FaPlus, FaEye, FaUsers } from "react-icons/fa";
import { BASE_URL, API_ENDPOINTS } from "../config/apiconfig";

function ViewCustomer() {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const navigate = useNavigate();

  const pageSize = 10;

  const navigateToPassbook = async (customerId, customerName) => {
    try {
      console.log(`Fetching passbook for customer: ${customerName} (ID: ${customerId})`);
      
      // Call the passbook API
      const response = await fetch(`${BASE_URL}/challans/passbook?name=${encodeURIComponent(customerName)}&page=0&size=10`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch passbook data');
      }
      
      const passbookData = await response.json();

      // Fetch the Passbook Summary (Product Balances)
      const summaryResponse = await fetch(`${BASE_URL}/challans/getPassbookSummary?id=${customerId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      const summaryData = summaryResponse.ok ? await summaryResponse.json() : [];

      console.log("Passbook data:", passbookData);
      console.log("Passbook summary:", summaryData);
      
      // Store passbook data
      localStorage.setItem('passbookData', JSON.stringify(passbookData));
      localStorage.setItem('passbookSummary', JSON.stringify(summaryData));
      
      // Show success message with data count
      toast.success(`Found ${passbookData.content?.length || 0} challans in passbook`);
      
      // Navigate to passbook page with data
      console.log("Navigating to passbook page with data:", {
        customerId,
        customerName,
        passbookData: passbookData
      });
      
      // Check if we have data before navigating
      if (passbookData && passbookData.content) {
        console.log("Passbook data summary:", {
          totalChallans: passbookData.content.length,
          totalPages: passbookData.totalPages,
          currentPage: passbookData.pageable?.pageNumber
        });
      }
      
      const navigationState = {
        customerId: customerId,
        customerName: customerName,
        fromViewCustomer: true,
        passbookData: passbookData,
        summaryData: summaryData
      };
      
      console.log("Navigation state being passed:", navigationState);
      
      navigate(`/dashboard/customers/passbook/${customerId}`, {
        state: navigationState
      });
      
      console.log("Navigation completed to passbook page");
      
    } catch (error) {
      console.error('Error fetching passbook:', error);
      toast.error("Failed to fetch passbook data");
      
      console.log("Attempting fallback navigation without data");
      
      // Fallback: Navigate without data
      try {
        navigate(`/dashboard/customers/passbook/${customerId}`, {
          state: {
            customerId: customerId,
            customerName: customerName,
            fromViewCustomer: true
          }
        });
        console.log("Fallback navigation completed");
      } catch (navError) {
        console.error("Navigation error:", navError);
        toast.error("Unable to navigate to passbook page");
        
        // Last resort: Try a simple navigation
        navigate(`/dashboard/customers/passbook/${customerId}`);
      }
    }
  };

  // ================= FETCH CUSTOMER =================
  const fetchCustomers = useCallback(async () => {
    setLoading(true);

    try {
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.CUSTOMERS.SEARCH}?page=${currentPage}&size=${pageSize}&name=${searchTerm}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) throw new Error();

      const data = await response.json();

      setCustomers(data.content || []);
      setTotalPages(data.totalPages || 0);

    } catch {
      toast.error("Error fetching customers");
      setCustomers([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    fetchCustomers();
  }, [currentPage, searchTerm, fetchCustomers]);

  // ================= SEARCH =================
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0);
  };

  // ================= DOWNLOAD PASSBOOK =================
  const downloadPassbook = async (customerId) => {
    try {
      const response = await fetch(
        `https://ctmapi.zerlak.com/api/v1/customers/${customerId}/passbook.pdf`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to download");

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `passbook-${customerId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      toast.success("Passbook downloaded successfully");

    } catch (err) {
      toast.error("Error downloading passbook");
    }
  };

  // ================= VIEW GOODS =================
  const viewGoods = (customerId) => {
    navigate(`/dashboard/customers/goods/${customerId}`);
  };

  // ================= EDIT =================
  const editCustomer = (customerId) => {
    navigate(`/dashboard/customers/create/${customerId}`);
  };

  // ================= DELETE =================
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this customer?")) return;

    try {
      const response = await fetch(`${BASE_URL}${API_ENDPOINTS.CUSTOMERS.DELETE_BY_ID(id)}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) throw new Error();

      toast.success("Customer deleted successfully");
      fetchCustomers();

    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div style={{ 
      padding: '20px',
      maxWidth: '100vw',
      overflowX: 'hidden',
      minHeight: '100vh',
      boxSizing: 'border-box'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '10px',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center',
          gap: '12px'
        }}>
          <FaUsers style={{ 
            fontSize: 'clamp(1.5rem, 4vw, 2rem)',
            color: '#3b82f6'
          }} />
          <h2 style={{ 
            margin: 0,
            fontSize: 'clamp(1.2rem, 4vw, 1.8rem)',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: '#1e293b'
          }}>
            View Customers
          </h2>
        </div>
        <Link 
          to="/dashboard/customers/create" 
          style={{
            padding: '10px 20px',
            border: '1px solid #3b82f6',
            borderRadius: '6px',
            backgroundColor: '#3b82f6',
            color: '#ffffff',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            textDecoration: 'none',
            boxShadow: '0 2px 4px rgba(59, 130, 246, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#2563eb';
            e.target.style.borderColor = '#2563eb';
            e.target.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.2)';
            e.target.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#3b82f6';
            e.target.style.borderColor = '#3b82f6';
            e.target.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.1)';
            e.target.style.transform = 'translateY(0)';
          }}
        >
          <FaPlus style={{ fontSize: '14px' }} />
          Create Customer
        </Link>
      </div>

      <div style={{ 
        marginBottom: '20px'
      }}>
        <input
          type="text"
          placeholder="Search customer..."
          value={searchTerm}
          onChange={handleSearch}
          style={{
            width: '100%',
            padding: '12px 16px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            fontSize: '14px',
            backgroundColor: '#ffffff',
            outline: 'none',
            boxSizing: 'border-box',
            transition: 'all 0.2s ease'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#3b82f6';
            e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#e2e8f0';
            e.target.style.boxShadow = 'none';
          }}
        />
      </div>

      <div style={{ 
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}>
        <div style={{ 
          overflowX: 'auto',
          overflowY: 'hidden',
          width: '100%',
          maxWidth: '100%'
        }}>
          {loading ? (
            <div style={{ 
              padding: '40px',
              textAlign: 'center',
              color: '#64748b',
              fontSize: '14px'
            }}>
              Loading...
            </div>
          ) : (
            <table style={{ 
              width: '100%',
              minWidth: '600px',
              maxWidth: '100%',
              borderCollapse: 'collapse',
              fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
              tableLayout: 'auto'
            }}>
              <thead>
                <tr>
                  <th style={{ 
                    padding: '12px 8px',
                    textAlign: 'center',
                    backgroundColor: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Name
                  </th>
                  <th style={{ 
                    padding: '12px 8px',
                    textAlign: 'center',
                    backgroundColor: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Mobile
                  </th>
                  <th style={{ 
                    padding: '12px 8px',
                    textAlign: 'center',
                    backgroundColor: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Email
                  </th>
                  <th style={{ 
                    padding: '12px 8px',
                    textAlign: 'center',
                    backgroundColor: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Address
                  </th>
                  <th style={{ 
                    padding: '12px 8px',
                    textAlign: 'center',
                    backgroundColor: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Passbook
                  </th>
                  <th style={{ 
                    padding: '12px 8px',
                    textAlign: 'center',
                    backgroundColor: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {customers.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ 
                      padding: '40px',
                      textAlign: 'center',
                      color: '#64748b',
                      fontSize: '14px',
                      border: 'none'
                    }}>
                      No Customers Found
                    </td>
                  </tr>
                ) : (
                  customers.map((customer) => (
                    <tr key={customer.id} style={{ 
                      borderBottom: '1px solid #f1f5f9',
                      transition: 'background-color 0.2s ease'
                    }}>
                      <td style={{ 
                        padding: '12px 8px',
                        textAlign: 'center',
                        color: '#1e293b',
                        fontWeight: '500',
                        maxWidth: '150px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                      <button 
                        onClick={() => navigateToPassbook(customer.id, customer.name)}
                        title={`View passbook for ${customer.name}`}
                        aria-label={`View passbook for ${customer.name}`}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#3b82f6',
                          cursor: 'pointer',
                          padding: '0',
                          margin: '0',
                          fontSize: 'inherit',
                          fontWeight: 'inherit',
                          textAlign: 'center',
                          textDecoration: 'underline',
                          textDecorationColor: 'rgba(59, 130, 246, 0.3)',
                          textUnderlineOffset: '2px',
                          transition: 'all 0.2s ease',
                          maxWidth: '150px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.color = '#2563eb';
                          e.target.style.textDecorationColor = '#2563eb';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = '#3b82f6';
                          e.target.style.textDecorationColor = 'rgba(59, 130, 246, 0.3)';
                        }}
                      >
                        {customer.name}
                      </button>
                      </td>
                      <td style={{ 
                        padding: '12px 8px',
                        textAlign: 'center',
                        color: '#64748b',
                        maxWidth: '120px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {customer.contactNumber}
                      </td>
                      <td style={{ 
                        padding: '12px 8px',
                        textAlign: 'center',
                        color: '#64748b',
                        maxWidth: '180px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {customer.email}
                      </td>
                      <td style={{ 
                        padding: '12px 8px',
                        textAlign: 'center',
                        color: '#64748b',
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {customer.address}
                      </td>

                      <td style={{ 
                        padding: '12px 8px',
                        textAlign: 'center'
                      }}>
                        <button
                          type="button"
                          onClick={() => downloadPassbook(customer.id)}
                          style={{
                            padding: '6px 12px',
                            border: '1px solid #3b82f6',
                            borderRadius: '4px',
                            backgroundColor: '#3b82f6',
                            color: '#ffffff',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            textDecoration: 'none'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#2563eb';
                            e.target.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#3b82f6';
                            e.target.style.transform = 'translateY(0)';
                          }}
                        >
                          <FaFilePdf style={{ fontSize: '12px' }} />
                          PDF
                        </button>
                      </td>

                      <td style={{ 
                        padding: '12px 8px',
                        textAlign: 'center'
                      }}>
                        <div style={{ 
                          display: 'flex',
                          gap: '4px',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}>
                          <button 
                            type="button" 
                            onClick={() => viewGoods(customer.id)}
                            style={{
                              padding: '4px 8px',
                              border: '1px solid #3b82f6',
                              borderRadius: '4px',
                              backgroundColor: 'transparent',
                              color: '#3b82f6',
                              fontSize: '11px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '2px'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#3b82f6';
                              e.target.style.color = '#ffffff';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                              e.target.style.color = '#3b82f6';
                            }}
                          >
                            <FaEye style={{ fontSize: '10px' }} />
                            View
                          </button>

                          <button 
                            type="button" 
                            onClick={() => editCustomer(customer.id)}
                            style={{
                              padding: '4px 8px',
                              border: '1px solid #f59e0b',
                              borderRadius: '4px',
                              backgroundColor: 'transparent',
                              color: '#f59e0b',
                              fontSize: '11px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '2px'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#f59e0b';
                              e.target.style.color = '#ffffff';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                              e.target.style.color = '#f59e0b';
                            }}
                          >
                            <FaEdit style={{ fontSize: '10px' }} />
                            Edit
                          </button>

                          <button 
                            type="button" 
                            onClick={() => handleDelete(customer.id)}
                            style={{
                              padding: '4px 8px',
                              border: '1px solid #ef4444',
                              borderRadius: '4px',
                              backgroundColor: 'transparent',
                              color: '#ef4444',
                              fontSize: '11px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '2px'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#ef4444';
                              e.target.style.color = '#ffffff';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                              e.target.style.color = '#ef4444';
                            }}
                          >
                            <FaTrash style={{ fontSize: '10px' }} />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div style={{ 
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '8px',
        marginTop: '20px',
        flexWrap: 'wrap'
      }}>
        <button
          disabled={currentPage === 0}
          onClick={() => setCurrentPage(currentPage - 1)}
          style={{
            padding: '8px 16px',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            backgroundColor: currentPage === 0 ? '#f8fafc' : '#ffffff',
            color: currentPage === 0 ? '#9ca3af' : '#374151',
            fontSize: '14px',
            fontWeight: '500',
            cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            outline: 'none'
          }}
          onMouseEnter={(e) => {
            if (currentPage !== 0) {
              e.target.style.backgroundColor = '#f8fafc';
              e.target.style.borderColor = '#3b82f6';
              e.target.style.color = '#3b82f6';
            }
          }}
          onMouseLeave={(e) => {
            if (currentPage !== 0) {
              e.target.style.backgroundColor = '#ffffff';
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.color = '#374151';
            }
          }}
        >
          Previous
        </button>

        {[...Array(totalPages).keys()].map((page) => (
          <button
            key={page}
            onClick={() => setCurrentPage(page)}
            style={{
              padding: '8px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              backgroundColor: currentPage === page ? '#3b82f6' : '#ffffff',
              color: currentPage === page ? '#ffffff' : '#374151',
              fontSize: '14px',
              fontWeight: currentPage === page ? '600' : '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              outline: 'none',
              minWidth: '36px'
            }}
            onMouseEnter={(e) => {
              if (currentPage !== page) {
                e.target.style.backgroundColor = '#f8fafc';
                e.target.style.borderColor = '#3b82f6';
                e.target.style.color = '#3b82f6';
              }
            }}
            onMouseLeave={(e) => {
              if (currentPage !== page) {
                e.target.style.backgroundColor = '#ffffff';
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.color = '#374151';
              }
            }}
          >
            {page + 1}
          </button>
        ))}

        <button
          disabled={currentPage === totalPages - 1}
          onClick={() => setCurrentPage(currentPage + 1)}
          style={{
            padding: '8px 16px',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            backgroundColor: currentPage === totalPages - 1 ? '#f8fafc' : '#ffffff',
            color: currentPage === totalPages - 1 ? '#9ca3af' : '#374151',
            fontSize: '14px',
            fontWeight: '500',
            cursor: currentPage === totalPages - 1 ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            outline: 'none'
          }}
          onMouseEnter={(e) => {
            if (currentPage !== totalPages - 1) {
              e.target.style.backgroundColor = '#f8fafc';
              e.target.style.borderColor = '#3b82f6';
              e.target.style.color = '#3b82f6';
            }
          }}
          onMouseLeave={(e) => {
            if (currentPage !== totalPages - 1) {
              e.target.style.backgroundColor = '#ffffff';
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.color = '#374151';
            }
          }}
        >
          Next
        </button>
      </div>

    </div>
  );
}

export default ViewCustomer;
